import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiDownload,
  FiFileText,
  FiHome,
  FiInfo,
  FiMapPin,
  FiMenu,
  FiRefreshCw,
  FiRss,
  FiUsers,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../components/Spinner";
import { toCalendarSubscriptionUrl } from "../../calendar/calendarSubscriptionUrl";
import { useParentYearPlannerExcel } from "../../calendar/useParentYearPlannerExcel";
import { translateClassName } from "../../../../utils/classroomUtils";

const INITIAL_VISIBLE_EVENTS = 10;
const VISIBLE_EVENTS_STEP = 10;
const FILTERS = ["all", "school", "class", "activity"];

const getLookupName = (classroomLookup, id) => {
  if (!id && id !== 0) return "";
  const key = String(id);
  if (classroomLookup instanceof Map) return classroomLookup.get(key) || "";
  return classroomLookup?.[key] || "";
};

const getClassroomName = (classroom) =>
  String(
    classroom?.className ||
      classroom?.classroomName ||
      classroom?.name ||
      classroom?.label ||
      classroom?.classroom?.className ||
      classroom?.classroom?.classroomName ||
      classroom?.classroom?.name ||
      classroom?.classroom?.label ||
      ""
  ).trim();

const getTranslatedClassroomName = (classroomOrName) => {
  if (typeof classroomOrName === "string" && classroomOrName.includes("·")) {
    return classroomOrName.trim();
  }

  const translated = translateClassName(classroomOrName);
  if (translated) return translated.trim();

  return typeof classroomOrName === "string"
    ? classroomOrName.trim()
    : getClassroomName(classroomOrName);
};

const stripTeacherTitle = (value) =>
  typeof value === "string" ? value.trim().replace(/^(juf|meester)\s+/i, "").trim() : "";

const getTeacherName = (classroom) => {
  const nested = classroom?.classroom || {};
  const teacher =
    classroom?.teacher ||
    classroom?.primaryTeacher ||
    nested?.teacher ||
    nested?.primaryTeacher ||
    (Array.isArray(classroom?.teachers) ? classroom.teachers[0] : null) ||
    (Array.isArray(nested?.teachers) ? nested.teachers[0] : null);

  const firstName = stripTeacherTitle(
    classroom?.teacherFirstName ||
      nested?.teacherFirstName ||
      teacher?.firstName ||
      teacher?.givenName ||
      teacher?.firstname
  );

  if (firstName) return firstName;

  return stripTeacherTitle(
    classroom?.teacherName ||
      classroom?.teacherFullName ||
      nested?.teacherName ||
      nested?.teacherFullName ||
      teacher?.displayName ||
      teacher?.fullName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ")
  );
};

const trimClassroomNumber = (value) => String(value || "").replace(/\s+\d+\s*$/, "").trim();

const getClassroomCalendarLabel = (classroom) => {
  const classroomName = getTranslatedClassroomName(classroom);
  const teacherName = getTeacherName(classroom);

  if (teacherName && classroomName) {
    return `${trimClassroomNumber(classroomName)} · ${teacherName}`;
  }

  if (teacherName) return teacherName;
  return classroomName;
};

const getClassroomIds = (classroom) =>
  [
    classroom?.id,
    classroom?.uuid,
    classroom?.classroomId,
    classroom?.classroomUuid,
    classroom?.classroom?.id,
    classroom?.classroom?.uuid,
  ]
    .filter((value) => value !== undefined && value !== null && String(value).trim())
    .map((value) => String(value));

const parseCalendarDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDayKey = (value) => {
  const date = parseCalendarDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCalendarDateParts = (value, locale = "en-US") => {
  const date = parseCalendarDate(value);
  if (!date) return { day: "-", weekday: "" };

  return {
    day: String(date.getDate()),
    weekday: date.toLocaleDateString(locale, { weekday: "short" }).replace(".", "").toLowerCase(),
  };
};

const getCalendarClassBadges = (event, classroomLookup, fallbackLabel) => {
  const badges = [];
  const seenIds = new Set();

  const addBadge = (id, directName, fallbackName) => {
    const key = id !== undefined && id !== null && String(id).trim() ? String(id) : "";
    if (key && seenIds.has(key)) return;
    if (key) seenIds.add(key);

    badges.push({
      key: key || `${badges.length}-${directName || fallbackName || fallbackLabel}`,
      label:
        getTranslatedClassroomName(directName || getLookupName(classroomLookup, key) || fallbackName) ||
        fallbackLabel,
    });
  };

  if (Array.isArray(event?.classrooms)) {
    event.classrooms.forEach((classroom) => {
      const ids = getClassroomIds(classroom);
      const name = getClassroomCalendarLabel(classroom);

      if (ids.length) addBadge(ids[0], name || ids.map((id) => getLookupName(classroomLookup, id)).find(Boolean));
      else if (name) addBadge("", name);
    });
  }

  const classroomNames = Array.isArray(event?.classroomNames) ? event.classroomNames.filter(Boolean) : [];
  const classroomIds = [
    ...(Array.isArray(event?.classroomUuids) ? event.classroomUuids : []),
    ...(Array.isArray(event?.classroomIds) ? event.classroomIds : []),
  ];

  classroomIds.forEach((id, index) => addBadge(id, "", classroomNames[index]));
  if (!badges.length && classroomNames.length) classroomNames.forEach((name) => addBadge("", String(name)));

  return badges.length ? badges : [{ key: "classroom-fallback", label: fallbackLabel }];
};

const CalendarScopeBadge = ({ tone = "school", label }) => (
  <span className={`pp-calendar-scope-badge is-${tone}`} title={label}>
    {label}
  </span>
);

const CalendarIconBubble = ({ tone, children }) => <span className={`pp-calendar-row-icon is-${tone}`}>{children}</span>;

const CalendarTab = ({
  t,
  calendarLoading,
  calendarError,
  groupedCalendarEvents,
  loadCalendarEvents,
  getCalendarClassNames,
  classroomLookup,
  formatCalendarDate,
  formatCalendarTime,
  schoolCalendarLink,
  classCalendarLinks,
  calendarCopied,
  copyCalendarUrl,
  schoolName,
  familyName,
}) => {
  const { i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === "en" || i18n.language?.startsWith("en") ? "en-US" : "nl-NL";
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const {
    download: downloadYearPlannerExcel,
    downloading: yearPlannerExcelDownloading,
    failed: yearPlannerExcelFailed,
  } = useParentYearPlannerExcel();
  const [calendarHelpOpen, setCalendarHelpOpen] = useState(false);
  const [connectPageOpen, setConnectPageOpen] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openingEventKey, setOpeningEventKey] = useState("");
  const [visibleEventCount, setVisibleEventCount] = useState(INITIAL_VISIBLE_EVENTS);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDayKey, setSelectedDayKey] = useState("");
  const openEventTimerRef = useRef(null);
  const calendarMenuRef = useRef(null);
  const monthSwipeRef = useRef({ y: 0, x: 0 });

  const subscriptionLinks = useMemo(
    () =>
      [
        {
          key: "school",
          id: "school",
          label: t("parentPortal.calendar.school_agenda", { defaultValue: "School calendar" }),
          url: schoolCalendarLink,
        },
        ...(classCalendarLinks || []).map((link) => ({
          key: link.id,
          ...link,
          label: getClassroomCalendarLabel(link) || getTranslatedClassroomName(link.label) || link.label,
        })),
      ]
        .filter((link) => link.url)
        .map((link) => ({ ...link, subscriptionUrl: toCalendarSubscriptionUrl(link.url) })),
    [classCalendarLinks, schoolCalendarLink, t]
  );

  const allEvents = useMemo(
    () =>
      (groupedCalendarEvents || []).flatMap((group) =>
        (group.events || []).map((event, index) => ({ event, groupKey: group.key, groupLabel: group.label, index }))
      ),
    [groupedCalendarEvents]
  );

  const getEventTone = (event) => {
    if (String(event?.scope || "").toUpperCase() === "CLASSROOM") return "class";
    if (event?.type === "ACTIVITY" || event?.activityType || event?.category === "ACTIVITY") return "activity";
    return "school";
  };

  const getEventViewModel = (event, groupKey, index) => {
    const eventKey = String(event.uuid || event.id || `${groupKey}-${index}`);
    const classNames = getCalendarClassNames(event);
    const tone = getEventTone(event);
    const isClassEvent = tone === "class";
    const scopeBadges = isClassEvent
      ? getCalendarClassBadges(event, classroomLookup, t("parentPortal.calendar.scope_class", { defaultValue: "Class" }))
      : [
          {
            key: tone,
            label:
              tone === "activity"
                ? t("parentPortal.calendar.scope_activity", { defaultValue: "Activity" })
                : t("parentPortal.calendar.scope_school", { defaultValue: "School" }),
          },
        ];

    const startParts = getCalendarDateParts(event.startDate, dateLocale);
    const endParts = getCalendarDateParts(event.endDate, dateLocale);
    const startDateLabel = formatCalendarDate(event.startDate);
    const endDateLabel = formatCalendarDate(event.endDate);
    const isMultiDay = startDateLabel !== endDateLabel;
    const timeLabel = isMultiDay ? `${startDateLabel} - ${endDateLabel}` : formatCalendarTime(event);
    const dayKey = toDayKey(event.startDate);

    return {
      event,
      eventKey,
      classNames,
      tone,
      isClassEvent,
      scopeBadges,
      startParts,
      endParts,
      startDateLabel,
      endDateLabel,
      isMultiDay,
      timeLabel,
      dayKey,
    };
  };

  const eventViewModels = useMemo(
    () => allEvents.map(({ event, groupKey, index }) => getEventViewModel(event, groupKey, index)),
    [allEvents, classroomLookup, dateLocale, getCalendarClassNames, formatCalendarDate, formatCalendarTime, t]
  );

  const filteredEvents = useMemo(() => {
    const result = activeFilter === "all" ? eventViewModels : eventViewModels.filter((vm) => vm.tone === activeFilter);
    if (!selectedDayKey) return result;
    return result.filter((vm) => vm.dayKey === selectedDayKey);
  }, [activeFilter, eventViewModels, selectedDayKey]);

  const totalEventCount = eventViewModels.length;
  const visibleEvents = filteredEvents.slice(0, visibleEventCount);
  const hasMoreEvents = visibleEventCount < filteredEvents.length;

  const eventsByDay = useMemo(() => {
    const map = new Map();
    eventViewModels.forEach((vm) => {
      if (!vm.dayKey) return;
      const dayEvents = map.get(vm.dayKey) || [];
      dayEvents.push(vm);
      map.set(vm.dayKey, dayEvents);
    });
    return map;
  }, [eventViewModels]);

  const firstEventDate = useMemo(() => {
    const first = eventViewModels.find((vm) => parseCalendarDate(vm.event.startDate));
    return first ? parseCalendarDate(first.event.startDate) : null;
  }, [eventViewModels]);

  useEffect(() => {
    if (firstEventDate && !selectedDayKey) {
      setCurrentMonth(new Date(firstEventDate.getFullYear(), firstEventDate.getMonth(), 1));
    }
  }, [firstEventDate, selectedDayKey]);

  useEffect(() => {
    setVisibleEventCount(INITIAL_VISIBLE_EVENTS);
  }, [activeFilter, selectedDayKey]);

  useEffect(() => {
    if (!selectedEvent) return;
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }
  }, [selectedEvent]);

  useEffect(
    () => () => {
      if (openEventTimerRef.current) window.clearTimeout(openEventTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!calendarMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!calendarMenuRef.current?.contains(event.target)) setCalendarMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setCalendarMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [calendarMenuOpen]);

  const handleCopyCalendarUrl = (link) => copyCalendarUrl(link.url, String(link.key || link.id));

  const handleOpenEvent = (viewModel) => {
    if (!viewModel) return;
    setOpeningEventKey(viewModel.eventKey);
    if (openEventTimerRef.current) window.clearTimeout(openEventTimerRef.current);
    openEventTimerRef.current = window.setTimeout(() => {
      setSelectedEvent(viewModel);
      setOpeningEventKey("");
      openEventTimerRef.current = null;
    }, 90);
  };

  const handleCloseEvent = () => {
    if (openEventTimerRef.current) {
      window.clearTimeout(openEventTimerRef.current);
      openEventTimerRef.current = null;
    }
    setOpeningEventKey("");
    setSelectedEvent(null);
  };

  const moveMonth = (amount) => {
    setCurrentMonth((month) => new Date(month.getFullYear(), month.getMonth() + amount, 1));
    setSelectedDayKey("");
  };

  const applyMonthSwipe = (clientX, clientY) => {
    const deltaY = clientY - monthSwipeRef.current.y;
    const deltaX = clientX - monthSwipeRef.current.x;

    if (Math.abs(deltaY) < 36 || Math.abs(deltaY) < Math.abs(deltaX) * 1.1) return;

    // swipe up = collapse
    if (deltaY < 0) {
      setCalendarExpanded(false);
      return;
    }

    // swipe down = expand
    setCalendarExpanded(true);
  };

  const handleMonthTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    monthSwipeRef.current = { y: touch.clientY, x: touch.clientX };
  };

  const handleMonthTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;

    const deltaY = touch.clientY - monthSwipeRef.current.y;
    const deltaX = touch.clientX - monthSwipeRef.current.x;

    if (Math.abs(deltaY) > 14 && Math.abs(deltaY) > Math.abs(deltaX) * 1.05) {
      event.preventDefault();
    }
  };

  const handleMonthTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    applyMonthSwipe(touch.clientX, touch.clientY);
  };

  const handleMonthPointerDown = (event) => {
    if (event.pointerType === "mouse") return;
    monthSwipeRef.current = { y: event.clientY, x: event.clientX };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleMonthPointerUp = (event) => {
    if (event.pointerType === "mouse") return;
    applyMonthSwipe(event.clientX, event.clientY);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const key = toDayKey(date);
      return {
        date,
        key,
        day: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isSelected: selectedDayKey === key,
        isToday: key === toDayKey(new Date()),
        events: eventsByDay.get(key) || [],
      };
    });
  }, [currentMonth, eventsByDay, selectedDayKey]);

  const monthTitle = currentMonth.toLocaleDateString(dateLocale, { month: "long", year: "numeric" });
  const displaySchoolName = schoolName || t("parentPortal.school.default_name", { defaultValue: "School" });
  const displayFamilyName = familyName || t("parentPortal.family", { defaultValue: "Family" });

  const selectedDateLabel = selectedDayKey
    ? parseCalendarDate(selectedDayKey)?.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long" })
    : t("parentPortal.calendar.today", { defaultValue: "Today" });

  const getFilterLabel = (filter) =>
    ({
      all: t("common.all", { defaultValue: "All" }),
      school: t("parentPortal.calendar.scope_school", { defaultValue: "School" }),
      class: t("parentPortal.calendar.scope_class", { defaultValue: "Class" }),
      activity: t("parentPortal.calendar.scope_activity", { defaultValue: "Activity" }),
    }[filter]);

  const getEventIcon = (tone) => {
    if (tone === "class") return <FiUsers aria-hidden="true" />;
    if (tone === "activity") return <FiCalendar aria-hidden="true" />;
    return <FiHome aria-hidden="true" />;
  };

  const getAgendaRowParts = (viewModel) => {
    if (!viewModel) {
      return { weekday: "", day: "-", time: "", meta: "" };
    }

    const rawTime = String(viewModel.timeLabel || "").trim();
    const allDayLabel = t("parentPortal.calendar.all_day", { defaultValue: "All day" });
    const time = rawTime
      ? rawTime
          .replace(/\s+-\s+/g, "–")
          .replace(/\s+–\s+/g, "–")
          .replace(/hele dag/i, allDayLabel)
      : allDayLabel;

    return {
      weekday: viewModel.startParts.weekday || "",
      day: viewModel.startParts.day || "-",
      time,
      meta: viewModel.event.location || viewModel.classNames || viewModel.scopeBadges?.[0]?.label || "",
    };
  };

  const renderCalendarMenu = () => (
    <div ref={calendarMenuRef} className="pp-calendar-options">
      <button
        type="button"
        onClick={() => setCalendarMenuOpen((open) => !open)}
        aria-expanded={calendarMenuOpen}
        aria-label={t("parentPortal.calendar.subscription_menu", { defaultValue: "Calendar options" })}
        className="pp-calendar-round-button"
      >
        <FiMenu aria-hidden="true" />
      </button>

      {calendarMenuOpen && (
        <div className="pp-calendar-menu pp-calendar-menu-compact">
          <button
            type="button"
            onClick={() => {
              setConnectPageOpen(true);
              setCalendarMenuOpen(false);
            }}
          >
            <FiRss aria-hidden="true" />
            <span>{t("parentPortal.calendar.connect_title", { defaultValue: "Connect calendar" })}</span>
          </button>

          <button
            type="button"
            onClick={downloadYearPlannerExcel}
            disabled={yearPlannerExcelDownloading}
          >
            <FiFileText aria-hidden="true" />
            <span>
              {yearPlannerExcelDownloading
                ? t("common.working", { defaultValue: "Working..." })
                : t("parentPortal.calendar.download_year_planner", { defaultValue: "Download year planner" })}
            </span>
          </button>
          {yearPlannerExcelFailed && (
            <p role="alert" className="px-2 py-1 text-xs text-rose-700 dark:text-rose-300">
              {t("parentPortal.calendar.download_year_planner_failed", {
                defaultValue: "The year planner could not be downloaded. Please try again later.",
              })}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setConnectPageOpen(true);
              setCalendarHelpOpen(true);
              setCalendarMenuOpen(false);
            }}
          >
            <FiInfo aria-hidden="true" />
            <span>{t("parentPortal.calendar.subscription_how_it_works", { defaultValue: "How does this work?" })}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              loadCalendarEvents?.();
              setCalendarMenuOpen(false);
            }}
            disabled={calendarLoading}
          >
            <FiRefreshCw aria-hidden="true" className={calendarLoading ? "is-spinning" : ""} />
            <span>{t("common.refresh", { defaultValue: "Refresh" })}</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderConnectPage = () => (
    <div key="calendar-connect" className="pp-calendar-app-page pp-calendar-connect-screen">
      <section className="pp-calendar-topbar pp-calendar-home-header pp-calendar-connect-topbar">
        <div className="pp-calendar-brand">
          <span className="pp-calendar-brand-icon"><FiHome aria-hidden="true" /></span>
          <span>
            <strong>{displaySchoolName}</strong>
            <small>{displayFamilyName}</small>
          </span>
        </div>

        <div className="pp-calendar-topbar-actions">
          {renderCalendarMenu()}
        </div>
      </section>

      <section className="pp-calendar-connect-header">
        <button type="button" onClick={() => setConnectPageOpen(false)} className="pp-calendar-floating-button">
          <FiChevronLeft aria-hidden="true" />
        </button>
        <span className="pp-calendar-connect-icon" aria-hidden="true"><FiRss /></span>
        <h2>{t("parentPortal.calendar.connect_title", { defaultValue: "Connect calendar" })}</h2>
        <p>
          {t("parentPortal.calendar.connect_page_subtitle", {
            defaultValue: "Add the school or class calendar to Apple Calendar, Google Calendar, Outlook, or another calendar app.",
          })}
        </p>
      </section>

      <section className="pp-calendar-connect-list">
        {!subscriptionLinks.length && (
          <div className="pp-calendar-empty">
            {t("parentPortal.calendar.no_subscription_links", { defaultValue: "No calendar links available." })}
          </div>
        )}

        {subscriptionLinks.map((link) => {
          const copied = calendarCopied === String(link.key || link.id);
          const isSchool = String(link.key || link.id) === "school";

          return (
            <article key={link.key || link.id} className="pp-calendar-connect-card">
              <div className="pp-calendar-connect-card-head">
                <CalendarIconBubble tone={isSchool ? "school" : "class"}>
                  {isSchool ? <FiHome aria-hidden="true" /> : <FiUsers aria-hidden="true" />}
                </CalendarIconBubble>
                <div>
                  <strong>{link.label}</strong>
                  <small>
                    {isSchool
                      ? t("parentPortal.calendar.scope_school", { defaultValue: "School" })
                      : t("parentPortal.calendar.scope_class", { defaultValue: "Class" })}
                  </small>
                </div>
              </div>

              <div className="pp-calendar-connect-actions">
                <a href={link.subscriptionUrl || link.url} className="pp-calendar-connect-primary">
                  <FiRss aria-hidden="true" />
                  {t("parentPortal.calendar.subscribe", { defaultValue: "Subscribe" })}
                </a>

                <button
                  type="button"
                  onClick={() => handleCopyCalendarUrl(link)}
                  className={copied ? "is-copied" : ""}
                >
                  <FiCopy aria-hidden="true" />
                  {copied
                    ? t("common.copied", { defaultValue: "Copied" })
                    : t("common.copy", { defaultValue: "Copy" })}
                </button>

                <a href={link.url} download>
                  <FiDownload aria-hidden="true" />
                  {t("common.download", { defaultValue: "Download" })}
                </a>
              </div>
            </article>
          );
        })}
      </section>

      <section className={`pp-calendar-connect-help ${calendarHelpOpen ? "is-open" : ""}`}>
        <button type="button" onClick={() => setCalendarHelpOpen((open) => !open)}>
          <span>
            <FiInfo aria-hidden="true" />
            {t("parentPortal.calendar.help_title", { defaultValue: "Help with setup" })}
          </span>
          <FiChevronRight aria-hidden="true" />
        </button>

        {calendarHelpOpen && (
          <div className="pp-calendar-connect-help-body">
            <p>
              {t("parentPortal.calendar.subscription_auto_updates", {
                defaultValue: "Calendar subscriptions update automatically. A downloaded file is a snapshot and does not update automatically.",
              })}
            </p>
            <p>
              {t("parentPortal.calendar.connect_help_mobile", {
                defaultValue: "On mobile, choose Subscribe first. If your phone does not open a calendar app, copy the link and paste it into your calendar app as a subscribed calendar.",
              })}
            </p>
          </div>
        )}
      </section>
    </div>
  );
  if (connectPageOpen) {
    return renderConnectPage();
  }

  if (selectedEvent) {
    const { event } = selectedEvent;

    return (
      <div key={`calendar-detail-${selectedEvent.eventKey}`} className="pp-calendar-app-page pp-calendar-detail-screen">
        <section className="pp-calendar-topbar pp-calendar-home-header pp-calendar-detail-header">
          <div className="pp-calendar-brand">
            <span className="pp-calendar-brand-icon"><FiHome aria-hidden="true" /></span>
            <span>
              <strong>{displaySchoolName}</strong>
              <small>{displayFamilyName}</small>
            </span>
          </div>

          <div className="pp-calendar-topbar-actions">
            {renderCalendarMenu()}
          </div>
        </section>

        <div className="pp-calendar-detail-hero">
          <button type="button" onClick={handleCloseEvent} className="pp-calendar-floating-button">
            <FiChevronLeft aria-hidden="true" />
          </button>

          <div className={`pp-calendar-detail-hero-icon is-${selectedEvent.tone}`} aria-hidden="true">
            <FiCalendar />
          </div>
        </div>

        <section className="pp-calendar-detail-sheet">
          <div className="pp-calendar-sheet-handle" aria-hidden="true" />

          <h2>{event.title || "-"}</h2>

          <div className="pp-calendar-detail-badges">
            {selectedEvent.scopeBadges.map((badge) => (
              <CalendarScopeBadge key={badge.key} tone={selectedEvent.tone} label={badge.label} />
            ))}
          </div>

          <div className="pp-calendar-detail-list">
            <div className="pp-calendar-detail-info-row">
              <CalendarIconBubble tone={selectedEvent.tone}><FiCalendar aria-hidden="true" /></CalendarIconBubble>
              <span>
                <strong>{selectedEvent.isMultiDay ? `${selectedEvent.startDateLabel} - ${selectedEvent.endDateLabel}` : selectedEvent.startDateLabel}</strong>
                <small>{selectedEvent.timeLabel || t("parentPortal.calendar.all_day", { defaultValue: "All day" })}</small>
              </span>
            </div>

            {event.location && (
              <div className="pp-calendar-detail-info-row">
                <CalendarIconBubble tone={selectedEvent.tone}><FiMapPin aria-hidden="true" /></CalendarIconBubble>
                <span>
                  <strong>{t("schoolCalendar.fields.location", { defaultValue: "Location" })}</strong>
                  <small>{event.location}</small>
                </span>
              </div>
            )}

            {selectedEvent.classNames && (
              <div className="pp-calendar-detail-info-row">
                <CalendarIconBubble tone="class"><FiUsers aria-hidden="true" /></CalendarIconBubble>
                <span>
                  <strong>{t("schoolCalendar.fields.classrooms", { defaultValue: "Classes" })}</strong>
                  <small>{selectedEvent.classNames}</small>
                </span>
              </div>
            )}

            {event.description && (
              <div className="pp-calendar-detail-info-row is-top">
                <CalendarIconBubble tone={selectedEvent.tone}><FiInfo aria-hidden="true" /></CalendarIconBubble>
                <span>
                  <strong>{t("schoolCalendar.fields.description", { defaultValue: "Details" })}</strong>
                  <small>{event.description}</small>
                </span>
              </div>
            )}
          </div>


          <button
            type="button"
            onClick={() => setConnectPageOpen(true)}
            className="pp-calendar-add-button"
          >
            <FiCalendar aria-hidden="true" />
            {t("parentPortal.calendar.add_to_my_calendar", { defaultValue: "Add to my calendar" })}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div key="calendar-list" className="pp-calendar-app-page pp-calendar-list-screen">
      <section className="pp-calendar-topbar pp-calendar-home-header">
        <div className="pp-calendar-brand">
          <span className="pp-calendar-brand-icon"><FiHome aria-hidden="true" /></span>
          <span>
            <strong>{displaySchoolName}</strong>
            <small>{displayFamilyName}</small>
          </span>
        </div>

        <div className="pp-calendar-topbar-actions">
          {renderCalendarMenu()}
        </div>
      </section>

      <section className="pp-calendar-page-title">
        <h2>{t("parentPortal.calendar.title", { defaultValue: "Agenda" })}</h2>
        <p>{t("parentPortal.calendar.mobile_subtitle", { defaultValue: "Planning & events" })}</p>
      </section>

      <section
        className={`pp-calendar-month-card pp-calendar-month-card-compact ${calendarExpanded ? "is-expanded" : "is-collapsed"}`}
        onTouchStart={handleMonthTouchStart}
        onTouchMove={handleMonthTouchMove}
        onTouchEnd={handleMonthTouchEnd}
        onPointerDown={handleMonthPointerDown}
        onPointerUp={handleMonthPointerUp}
      >
        <div className="pp-calendar-month-summary">
          <CalendarIconBubble tone="school"><FiCalendar aria-hidden="true" /></CalendarIconBubble>
          <button
            type="button"
            className="pp-calendar-month-summary-button"
            onClick={() => setCalendarExpanded((expanded) => !expanded)}
            aria-expanded={calendarExpanded}
          >
            <span>
              <strong>{monthTitle}</strong>
              <small>{t("parentPortal.calendar.event_count", { defaultValue: "{{count}} events", count: totalEventCount })}</small>
            </span>
            <FiChevronRight aria-hidden="true" />
          </button>
        </div>

        {calendarExpanded && (
          <>
            <div className="pp-calendar-month-head pp-calendar-month-head-compact">
              <button type="button" onClick={() => moveMonth(-1)}><FiChevronLeft aria-hidden="true" /></button>
              <h2>{monthTitle}</h2>
              <button type="button" onClick={() => moveMonth(1)}><FiChevronRight aria-hidden="true" /></button>
            </div>

            <div className="pp-calendar-weekdays" aria-hidden="true">
              {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day) => <span key={day}>{day}</span>)}
            </div>

            <div className="pp-calendar-month-grid">
              {calendarDays.map((day) => (
                <button
                  type="button"
                  key={day.key}
                  onClick={() => setSelectedDayKey((current) => (current === day.key ? "" : day.key))}
                  className={`${day.isCurrentMonth ? "" : "is-muted"} ${day.isSelected ? "is-selected" : ""} ${day.isToday ? "is-today" : ""}`}
                >
                  <span>{day.day}</span>
                  {day.events.length > 0 && <i aria-hidden="true" />}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="pp-calendar-swipe-handle" aria-hidden="true" />
      </section>

      <section className="pp-calendar-filter-row" aria-label={t("parentPortal.calendar.filters", { defaultValue: "Calendar filters" })}>
        {FILTERS.map((filter) => (
          <button
            key={filter}
            type="button"
            onClick={() => setActiveFilter(filter)}
            className={activeFilter === filter ? "is-active" : ""}
          >
            {getFilterLabel(filter)}
          </button>
        ))}
      </section>

      {calendarError && <div className="pp-calendar-error">{calendarError}</div>}

      <section className="pp-calendar-agenda-list">
        <h3>
          {selectedDateLabel}
          <span> · {filteredEvents.length}</span>
        </h3>

        {calendarLoading ? (
          <div className="pp-calendar-loading"><Spinner theme="parent" /></div>
        ) : !filteredEvents.length ? (
          <div className="pp-calendar-empty">
            {t("parentPortal.calendar.empty", { defaultValue: "There are no upcoming calendar items." })}
          </div>
        ) : (
          <div className="pp-calendar-event-list">
            {visibleEvents.map((vm) => {
              const rowParts = getAgendaRowParts(vm);

              return (
                <button
                  key={vm.eventKey}
                  type="button"
                  onClick={() => handleOpenEvent(vm)}
                  disabled={openingEventKey === vm.eventKey}
                  aria-busy={openingEventKey === vm.eventKey}
                  className={`pp-calendar-event-card is-${vm.tone} ${openingEventKey === vm.eventKey ? "is-opening" : ""}`}
                >
                  <div className="pp-calendar-event-date-column" aria-label={`${rowParts.weekday} ${rowParts.day}`}>
                    <span className="pp-calendar-event-weekday">{rowParts.weekday}</span>
                    <strong className="pp-calendar-event-day-number">{rowParts.day}</strong>
                  </div>

                  <CalendarIconBubble tone={vm.tone}>{getEventIcon(vm.tone)}</CalendarIconBubble>

                  <div className="pp-calendar-event-main">
                    <div className="pp-calendar-event-topline">
                      <strong>{vm.event.title || "-"}</strong>
                    </div>
                    <p>{rowParts.meta}</p>
                  </div>

                  <span className="pp-calendar-event-row-time">{rowParts.time}</span>
                  <FiChevronRight className="pp-calendar-event-chevron" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        )}

        {hasMoreEvents && (
          <button type="button" onClick={() => setVisibleEventCount((count) => count + VISIBLE_EVENTS_STEP)} className="pp-calendar-show-more">
            {t("parentPortal.calendar.show_more", { defaultValue: "Show more" })}
          </button>
        )}
      </section>

    </div>
  );
};

export default CalendarTab;
